import {
  Box,
  Button,
  Dot,
  Icon,
  Separator,
  Skeleton,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "@salestrak/ui";
import { JobTitleSelector } from "#app/components/entity-selectors";
import { EntityVisual } from "#app/components/entity/entity-visual";
import { ProfileAvatar } from "#app/components/settings/profile-avatar";
import { resolveEntityLabel, type EntityData } from "#app/lib/entity-visuals";
import type { KnownMember, StatusChip } from "./types";

/**
 * Identity header: avatar + panel actions on a top row, then the name and
 * title/status stacked BELOW at full width — so the title has room to
 * breathe and only truncates near the container edge, not early.
 */
export function MemberProfileHeader({
  userId,
  entity,
  known,
  identityLoading,
  former,
  statusChip,
  isSelf,
  canEdit,
  canManage,
  currentUserId,
  onEditProfile,
  onTitleChange,
  onClose,
}: {
  userId: string | null;
  entity: EntityData;
  known: KnownMember | null;
  /** Neither the fetch nor the roster has this member's identity yet. */
  identityLoading: boolean;
  former: boolean;
  statusChip: StatusChip;
  isSelf: boolean;
  /** `onEditProfile` is wired and there is a member to edit. */
  canEdit: boolean;
  canManage: boolean;
  currentUserId: string | null;
  onEditProfile?: ((userId: string) => void) | undefined;
  onTitleChange: (title: string | null) => void;
  onClose: () => void;
}) {
  const displayName = resolveEntityLabel(entity);
  const editLabel = isSelf ? "Edit profile" : "Manage member";
  const editIcon = isSelf ? ("pencil" as const) : ("settings" as const);

  return (
    <Box direction="column" gap="md" paddingTop="md" paddingX="md">
      <Box align="start" direction="row" justify="between">
        {identityLoading ? (
          <Skeleton circle delayed height={64} width={64} />
        ) : canEdit && known ? (
          // Same avatar editor as the settings page (color palette, photo
          // upload/removal) for whoever may edit this profile — self, or a
          // manage.users holder viewing another member.
          <ProfileAvatar presence size="xl" user={known} />
        ) : (
          // Profile surface — true avatar color, not the viewer-themed
          // treatment the chrome uses for the signed-in user.
          <EntityVisual entity={entity} presence size="xl" trueAvatarColor />
        )}
        <Box align="center" direction="row" gap="xs" shrink={false}>
          {canEdit && (
            // Revealed on hover anywhere over the island ("pane" scope);
            // on touch, once a tap anywhere on the island has armed it.
            <Box hoverReveal="pane">
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      aria-label={editLabel}
                      onClick={() => onEditProfile!(userId!)}
                      size="icon-sm"
                      variant="ghost"
                    >
                      <Icon name={editIcon} size="sm" />
                    </Button>
                  }
                />
                <TooltipPopup side="bottom">{editLabel}</TooltipPopup>
              </Tooltip>
            </Box>
          )}
          <Button
            aria-label="Close profile"
            onClick={onClose}
            size="icon-sm"
            color="neutral"
            ink="foreground"
          >
            <Icon name="x" size="sm" />
          </Button>
        </Box>
      </Box>

      <Box direction="column" gap="2xs">
        {identityLoading ? (
          <Box direction="column" gap="xs">
            <Skeleton delayed height={20} width={160} />
            <Skeleton delayed height={14} width={120} />
          </Box>
        ) : (
          <>
            <Text
              overflowTooltip
              size="lg"
              truncate={1}
              variant={former ? "muted" : undefined}
              weight="semibold"
            >
              {displayName}
            </Text>
            {/* Title (editable) · live presence — full width, title truncates
                last. The 24px squared ghost trigger's text sits 8px inside
                its box (the xs rung's 7px pad + 1px transparent border), so
                the editable picker bleeds start-ward by exactly that and the
                title text aligns flush under the name; read-only renders bare
                text and needs no bleed. */}
            <Box align="center" direction="row" gap="sm">
              {/* Flex, not block: an inline-flex button inside a block Box
                  picks up the line box's descender and grows the row to 25px. */}
              <Box
                align="center"
                direction="row"
                marginLeftNegative={canEdit ? "sm" : undefined}
                minWidth={0}
                shrink
              >
                <JobTitleSelector
                  canManage={canManage}
                  currentUserId={currentUserId}
                  onValueChange={onTitleChange}
                  readOnly={!canEdit}
                  shape="square"
                  size="xs"
                  value={known?.title ?? null}
                />
              </Box>
              {/* Hairline divider between the title and the live status. */}
              <Separator length={12} orientation="vertical" />
              <Box align="center" direction="row" gap="xs" shrink={false}>
                <Dot color={statusChip.color} />
                <Text color={statusChip.color} size="sm">
                  {statusChip.label}
                </Text>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
